// Shared data loader used by every pure-client report. Pulls the three
// window-scoped search roots once (sessions, tasks, legs) and shares
// them across report tabs so switching tabs doesn't re-fetch.
//
// Caller owns the from/to window via the shared Analytics date-range form.
// Each report derives its view from the same base dataset.
//
// Pagination: the Cisco API returns at most 500 rows per call and exposes
// NO pagination argument (probed 2026-10-26: after/cursor/offset/skip/
// limit/first/size/page/pageSize all rejected). So we use adaptive
// bisection via `fetchAllPaged` — pull the whole window first; if the
// server flags `hasNextPage: true`, split the window in half and recurse
// until every leaf is non-truncated. Dedup by primary key at merge time.
//
// Cost: a 24h window with ~2k sessions typically fetches as 1-4 slices per
// root (3-12 calls total, ~7-30s). A 7-day window with ~15k sessions
// typically needs ~30-60 slices per root — several minutes but complete.
// Progress is exposed via `progress` state so the UI can render a bar.

import { useCallback, useRef, useState } from 'react';
import { fetchAllPaged } from '../../../api_scripts/wxccPagingClient';

const AI_SESSIONS_QUERY = `
  query AiSessions($from: Long!, $to: Long!) {
    contactAIAgentSession(from: $from, to: $to) {
      data {
        interactionId sessionId agentId agentName agentType agentLanguage
        agentAIEngine interactionOutcome intent channelType ani dnis
        entryPoint { id name }
        flow { id name }
        sessionStartTime sessionEndTime duration
        noOfTotalMessages averageResponseTime totalIntentCompleted
        dialogCount lastAction totalActionCompleted
        deadAirCount evalInteractionsFailureCount bargeInCount
        fulfillmentCount welcomeMessageResponseTime
        overallEvalScore wordRatioTotalScore
      }
      pageInfo { endCursor hasNextPage }
    }
  }
`;

const TASK_DETAILS_QUERY = `
  query TaskDetails($from: Long!, $to: Long!) {
    taskDetails(from: $from, to: $to) {
      tasks {
        id status channelType direction createdTime endedTime totalDuration
        firstQueueId firstQueueName
        lastQueue { id name }
        lastAgent { id name }
        lastTeam { id name }
        lastSite { id name }
        entryPoint { id name }
        terminationType terminationReason isContactHandled
        lastWrapupCodeName
      }
      pageInfo { endCursor hasNextPage }
    }
  }
`;

const TASK_LEG_DETAILS_QUERY = `
  query TaskLegDetails($from: Long!, $to: Long!) {
    taskLegDetails(from: $from, to: $to) {
      taskLegs {
        id taskId createdTime endedTime channelType direction handleType
        callLegType handleTime ringingDuration connectedDuration
        holdDuration wrapupDuration terminationReason abandonedType
        queue { id name }
        owner { id name }
        team { id name }
        site { id name }
      }
      pageInfo { endCursor hasNextPage }
    }
  }
`;

const INITIAL_PROGRESS = {
  sessions: { phase: 'idle', slicesTotal: 0, bisections: 0, rowsLoaded: 0 },
  tasks:    { phase: 'idle', slicesTotal: 0, bisections: 0, rowsLoaded: 0 },
  legs:     { phase: 'idle', slicesTotal: 0, bisections: 0, rowsLoaded: 0 },
};

export function useReportWindow() {
  const [state, setState] = useState({ loading: false, data: null, error: null, fetchedAt: null, from: null, to: null, progress: INITIAL_PROGRESS });
  const abortRef = useRef(null);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const fetchWindow = useCallback(async (from, to) => {
    // Cancel any in-flight fetch before starting a new one.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setState({ loading: true, data: null, error: null, fetchedAt: null, from, to, progress: INITIAL_PROGRESS });

    const onProgress = (bucket) => (p) => {
      setState((prev) => ({
        ...prev,
        progress: {
          ...prev.progress,
          [bucket]: {
            phase: p.phase,
            slicesTotal: p.slicesTotal,
            bisections: p.bisections,
            rowsLoaded: p.rowsLoaded,
          },
        },
      }));
    };

    try {
      // Serialise the three roots — the Cisco API rate-limits globally at
      // ~1 req/2s, not per root. Running them in parallel just burns rate.
      const sessionsResult = await fetchAllPaged(
        AI_SESSIONS_QUERY, 'contactAIAgentSession', 'data', 'sessionId',
        { from, to, onProgress: onProgress('sessions'), signal: controller.signal },
      );
      if (controller.signal.aborted) throw new Error('aborted');

      const tasksResult = await fetchAllPaged(
        TASK_DETAILS_QUERY, 'taskDetails', 'tasks', 'id',
        { from, to, onProgress: onProgress('tasks'), signal: controller.signal },
      );
      if (controller.signal.aborted) throw new Error('aborted');

      const legsResult = await fetchAllPaged(
        TASK_LEG_DETAILS_QUERY, 'taskLegDetails', 'taskLegs', 'id',
        { from, to, onProgress: onProgress('legs'), signal: controller.signal },
      );

      setState((prev) => ({
        ...prev,
        loading: false,
        data: {
          sessions: sessionsResult.rows,
          sessionsMeta: sessionsResult.meta,
          sessionsTruncated: sessionsResult.meta.truncated,
          tasks: tasksResult.rows,
          tasksMeta: tasksResult.meta,
          tasksTruncated: tasksResult.meta.truncated,
          legs: legsResult.rows,
          legsMeta: legsResult.meta,
          legsTruncated: legsResult.meta.truncated,
        },
        error: null,
        fetchedAt: Date.now(),
        from,
        to,
      }));
    } catch (e) {
      if (controller.signal.aborted) {
        setState((prev) => ({ ...prev, loading: false, error: 'Cancelled before completion', fetchedAt: null }));
      } else {
        setState((prev) => ({ ...prev, loading: false, error: e.message, fetchedAt: null }));
      }
    }
  }, []);

  return { ...state, fetchWindow, cancel };
}
